import { InputColumnConfig } from "../../components/common/table_input/tableInput";
import { SQLTableProps } from "../../components/sql_table/tableMain";

export interface InputConfig {
    title: string;
    columns: InputColumnConfig[];
}

export const NAME_CORRECTION_TYPES = [
    { value: 'producer', label: 'Producer' },
    { value: 'lease', label: 'Lease' },
    { value: 'destination', label: 'Destination' },
];

export const MONTH_OPTIONS = [
    { value: 'January', label: 'January' },
    { value: 'February', label: 'February' },
    { value: 'March', label: 'March' },
    { value: 'April', label: 'April' },
    { value: 'May', label: 'May' },
    { value: 'June', label: 'June' },
    { value: 'July', label: 'July' },
    { value: 'August', label: 'August' },
    { value: 'September', label: 'September' },
    { value: 'October', label: 'October' },
    { value: 'November', label: 'November' },
    { value: 'December', label: 'December' }
];


export const getInputConfigs = (
    activeTable: string,
    producerOptions: any[],
    entityOptions: any[],
    leaseOptions: any[],
): Record<string, InputConfig> => ({
    producer_definition: {
        title: 'Producer Definition',
        columns: [
            { colName: 'producer_name', placeholder: 'Producer', colType: 'text', required: true },
            { colName: 'legal_entity_name', placeholder: 'Legal Name', colType: 'text', required: false },
        ],
    },
    entity_definition: {
        title: 'Entity Definition',
        columns: [
            { colName: 'entity_name', placeholder: 'Entity Name', colType: 'text', required: true },
        ],
    },
    lease_coordinates: {
        title: 'Lease Coordinates',
        columns: [
            {
                colName: 'producer_id',
                placeholder: 'Producer',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: producerOptions
            },
            {
                colName: 'entity_id',
                placeholder: 'Entity',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: entityOptions,
                dependsOn: 'producer_id',
                getFilteredOptions: (producerId: number, entityOptions: any[]) => {
                    const filtered = entityOptions.filter(entity => {
                        return Number(entity.current_producer_id) === Number(producerId);
                    });
                    return filtered;
                },
            },
            {
                colName: 'lease',
                placeholder: 'Lease Name',
                colType: 'text',
                required: true
            },
            {
                colName: 'latitude',
                placeholder: 'Latitude',
                colType: 'coordinate',
                required: false
            },
            {
                colName: 'longitude',
                placeholder: 'Longitude',
                colType: 'coordinate',
                required: false,
            },
        ],
    },
    market_coordinates: {
        title: 'Station Coordinates',
        columns: [
            { colName: 'market', placeholder: 'Station Owner', colType: 'text', required: true },
            { colName: 'station', placeholder: 'Station', colType: 'text', required: true },
        ],
    },
    mobile_req_mailing_list: {
        title: 'Mobile Req. Mailing Lists',
        columns: [
            { colName: 'producer_id', placeholder: 'Producer', colType: 'number', required: true, inputType: 'dropdown', dropdownOptions: producerOptions },
            { colName: 'mailing_list', placeholder: 'Mailing List', colType: 'text', required: true },
        ],
    },
    pumper_contact: {
        title: 'Pumper Contact Info',
        columns: [
            { 
                colName: 'producer_id',
                placeholder: 'Producer',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: producerOptions,
            },
            {
                colName: 'entity_id',
                placeholder: 'Entity',
                colType: 'number',
                required: false,
                inputType: 'dropdown',
                dropdownOptions: entityOptions,
                dependsOn: 'producer_id',
                getFilteredOptions: (producerId: number, entityOptions: any[]) => {
                    const filtered = entityOptions.filter(entity => {
                        return Number(entity.current_producer_id) === Number(producerId);
                    });
                    return filtered;
                },
            },
            {
                colName: 'name',
                placeholder: 'Name',
                colType: 'text',
                required: true,
            },
            {
                colName: 'pumper_email',
                placeholder: 'Email',
                colType: 'text',
                required: false,
            },
            {
                colName: 'pumper_phone',
                placeholder: 'Phone',
                colType: 'text',
                required: false,
            },
        ],
    },
    customer_reports: {
        title: 'Customer Reports',
        columns: [
            { colName: 'customer', placeholder: 'Customer', colType: 'text', required: true, inputType: 'dropdown', dropdownOptions: producerOptions },
        ],
    },
    trucking_rates: {
        title: 'Trucking Rates',
        columns: [
            { colName: 'mileage', placeholder: 'Mileage', colType: 'number', required: true },
        ],
    },
    carrier_definition: {
        title: 'Carrier Definition',
        columns: [
            { colName: 'name', placeholder: 'Carrier Name', colType: 'text', required: true },
        ],
    },
});

export const getColConfigs = (
    entityOptions: any[],
    producerOptions: any[],
): Record<string, Omit<SQLTableProps, 'tableData'>> => ({
    producer_definition: {
        tableName: 'Producer Definition',
        columnConfigs: [
            {
                colName: 'id',
                colHeader: 'ID',
                colType: 'number',
                editable: false,
            },
            { 
                colName:'is_active', 
                colHeader: 'Active', 
                colType: 'boolean', 
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                ],
                editable: true, 
                requiresApproval: true,
                editableLevel:2,
                approvalLevel: 4,
                rolesNotAllowedToEdit: ["viewer"],
            },
            { 
                colName: 'producer_name',
                colHeader: 'Producer Name',
                colType: 'text',
                editable: true,
                requiresApproval: true, 
                editableLevel: 2,
                approvalLevel: 4,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]
            },
            { 
                colName: 'legal_entity_name', 
                colHeader: 'Legal Name', 
                colType: 'text', 
                editable: true, 
                requiresApproval: true, 
                editableLevel: 2,
                approvalLevel: 4, 
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]
            },
            {
                colName: 'has_oil',
                colHeader: 'Oil Enabled',
                colType: 'boolean',
                editable: true,
                requiresApproval: true,
                approvalLevel: 4,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                ],
            },
            {
                colName: 'has_vac',
                colHeader: 'Vacuum Enabled',
                colType: 'boolean',
                editable: true,
                requiresApproval: true,
                approvalLevel: 4,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                ],
            },
            {
                colName: 'has_ngl',
                colHeader: 'NGL Enabled',
                colType: 'boolean',
                editable: true,
                requiresApproval: true,
                approvalLevel: 4,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                ],
            },
        ],
        sortOrder: { 
            'is_active': 'desc',
            'producer_name': 'asc',
        },
        idColumn: 'id',
        deletableRows: false,
    },
    entity_definition: {
        tableName: 'Entity Definition',
        columnConfigs: [
            {
                colName: 'id',
                colHeader: 'ID',
                colType: 'number',
                editable: false,
            },
            {
                colName:'is_active',
                colHeader: 'Active',
                colType: 'boolean',
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                ],
                editable: true,
                requiresApproval: true,
                editableLevel:2,
                approvalLevel: 4,
                rolesNotAllowedToEdit: ["viewer"],
            },
            {
                colName: 'current_producer_name',
                colHeader: 'Current Producer', 
                colType: 'text',
                editable: false,
            },
            {
                colName: 'entity_name',
                colHeader: 'Entity Name',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                approvalLevel: 4,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
            },
            {
                colName: 'legal_name', 
                colHeader: 'Legal Name', 
                colType: 'text',
                editable: true, 
                requiresApproval: true, 
                editableLevel: 2,
                approvalLevel: 4, 
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]
            },
            { 
                colName: 'producer_history',
                colHeader: 'Producer History',
                colType: 'json',
                editable: true,
            },
        ],
        jsonColumnConfig: [
            {
                pointsTo: 'producer_history',
                panelHeader: (rowData: any) => rowData.entity_name,
                SQLTable: {
                    preFilterData: { column: 'entity_id' }, // this will filter the table based on col entity_id matching parent row id
                    tableName: 'entity_producer_history',
                    idColumn: 'id',
                    columnConfigs: [
                        {
                            colName: 'producer_name',
                            colHeader: 'Producer Name',
                            colType: 'text',
                            editable: false,
                        },
                        {
                            colName: 'start_month',
                            colHeader: 'Start Month',
                            colType: 'number',
                            editable: true,
                        },
                        {
                            colName: 'start_year',
                            colHeader: 'Start Year',
                            colType: 'number',
                            editable: true,
                        },
                        {
                            colName: 'end_month',
                            colHeader: 'End Month',
                            colType: 'number',
                            editable: true,
                        },
                        {
                            colName: 'end_year',
                            colHeader: 'End Year',
                            colType: 'number',
                            editable: true,
                        },
                    ],
                    sortOrder: { 
                        'start_year': 'desc',
                        'start_month': 'desc',
                    },
                },
                tableInput: [{
                    colName: 'producer_id',
                    placeholder: 'Add Producer',
                    colType: 'number',
                    inputType: 'dropdown',
                    dropdownOptions: producerOptions,
                    required: true,
                }],                
            },
        ],
        sortOrder: { 
            'is_active': 'desc',
            'current_producer_name': 'asc',
            'entity_name': 'asc',
        },
        idColumn: 'id',
        deletableRows: true,
    },
    nominations: {
        tableName: 'Nominations',
        columnConfigs: [
            { colName: 'id', colHeader: 'ID', colType: 'number', editable: false },
            { colName: 'station_owner', colHeader: 'Station Owner', colType: 'text', editable: true, requiresApproval: true, editableLevel: 2,  approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'station', colHeader: 'Station', colType: 'text', editable: true, requiresApproval: true, editableLevel: 2, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'market', colHeader: 'Market', colType: 'text', editable: true, requiresApproval: true, editableLevel: 2, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]},
            { colName: 'month', colHeader: 'Month', colType: 'text', editable: true, requiresApproval: true, editableLevel: 2, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]},
            { colName: 'year', colHeader: 'Year', colType: 'text', editable: true, requiresApproval: true, editableLevel: 2, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]},
            { colName: 'nomination_monthly', colHeader: 'Monthly Nomination', colType: 'number', editable: true, requiresApproval: true, editableLevel: 2, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]},
            { colName: 'nomination_bpd', colHeader: 'Nomination BPD', colType: 'number', editable: true, editableLevel: 2, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
        ],
        sortBy: ['year', 'month'],
        idColumn: 'id',
        deletableRows: true, 
    },
    lease_coordinates: {
        tableName: 'Lease Coordinates',
        columnConfigs: [
            { colName: 'id', colHeader: 'ID', colType: 'text', editable: false },
            { 
                colName: 'is_active',
                colHeader: 'Active',
                colType: 'boolean',
                editable: true,
                requiresApproval: true,
                approvalLevel: 4,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                ],
            },
            {
                colName: 'producer_name',
                colHeader: 'Producer',
                colType: 'text',
                editable: false
            },
            {
                colName: 'entity_name',
                actualColName: 'entity_id',
                colHeader: 'Entity Name',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: entityOptions,
                dependsOn: 'producer_id',
                getFilteredOptions: (producerId: number, allLeases: any[]) => {
                    const filtered = allLeases.filter(lease => {
                        return Number(lease.current_producer_id) === Number(producerId);
                    });
                    return filtered;
                },
            },
            { colName: 'lease', colHeader: 'Lease', colType: 'text', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'group_split', colHeader: 'Field', colType: 'text', editable: true, editableLevel: 4 },
            { colName: 'tanks', colHeader: 'Tanks', colType: 'json', editable: true, editableLevel:4 },
            { 
                colName: 'product_type', 
                colHeader: 'Product Type', 
                colType: 'text', 
                editable: true, 
                requiresApproval: true, 
                editableLevel: 2, 
                approvalLevel: 4, 
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] ,
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Skim Oil', label: 'Skim Oil' },
                    { value: 'Condensate', label: 'Condensate' },
                    { value: 'Sour Crude', label: 'Sour Crude' },
                    { value: 'NGL', label: 'NGL' },
                    { value: 'Water', label: 'Water' },
                    { value: 'Lease Crude', label: 'Lease Crude' },
                ],
            },
            {
                colName: 'region',
                colHeader: 'Region',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                approvalLevel: 4,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Permian',     label: 'Permian' },
                    { value: 'Eagle_Ford',  label: 'Eagle Ford' },
                    { value: 'Bakken',      label: 'Bakken' },
                    { value: 'MidCon',      label: 'MidCon' },
                    { value: 'Marcellus',   label: 'Marcellus' },
                    { value: 'Utica',       label: 'Utica' },
                    { value: 'Haynesville', label: 'Haynesville' },
                    { value: 'DJ',          label: 'DJ' },
                ],
            },
            { colName: 'latitude', colHeader: 'Latitude', colType: 'coordinate', editable: true, requiresApproval: true, editableLevel: 2, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'longitude', colHeader: 'Longitude', colType: 'coordinate', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'origin_coordinates', colHeader: 'Coordinates', colType: 'coordinate', editable: false },
            { colName: 'state', colHeader: 'State', colType: 'text', editable: false },
            { colName: 'county', colHeader: 'County', colType: 'text', editable: false },
            { colName: 'customer_nickname', colHeader: 'Customer Nickname', colType: 'text', editable: true, requiresApproval: true, editableLevel: 2, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
        ],
        jsonColumnConfig: [
            {
                pointsTo: 'tanks',
                panelHeader: (rowData: any) => rowData.lease,
                SQLTable: {
                    preFilterData: { column: 'lease_id' }, // this will filter the table based on col lease_id matching parent row id
                    tableName: 'crude_tanks_definition',
                    idColumn: 'id',
                    columnConfigs: [
                        {
                            colName: 'tank_name',
                            colHeader: 'Tank',
                            colType: 'text',
                            editable: true,
                        },
                        {
                            colName: 'product',
                            colHeader: 'Product',
                            colType: 'text',
                            editable: true,
                        },
                        {
                            colName: 'bpi',
                            colHeader: 'BPI',
                            colType: 'number',
                            editable: true,
                        },
                    ],
                },
                tableInput: [{
                    colName: 'tank_name',
                    placeholder: 'Add Tank',
                    colType: 'text',
                    required: true,
                }],                
            },
        ],
        sortOrder: {
            'is_active': 'desc',
            'producer_name': 'asc',
            'lease': 'asc',
        },
        idColumn: 'id',
        deletableRows: true,
        deletionRequiresApproval: true,
    },
    market_coordinates: {
        tableName: 'Station Coordinates',
        columnConfigs: [
            { colName: 'id', colHeader: 'ID', colType: 'text', editable: false },
            { colName: 'is_active', colHeader: 'Active', colType: 'boolean', inputType: 'dropdown',
                dropdownOptions: [
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                ], editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["viewer"] },
            { colName: 'market', colHeader: 'Station Owner', colType: 'text', editable: false },
            { colName: 'station', colHeader: 'Station', colType: 'text', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'offload_lanes', colHeader: 'Offload Lanes', colType: 'text', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'offload_lane', colHeader: 'Offload Lanes JSON', colType: 'json', jsonCols: ['offload_lane_name']},
            { colName: 'latitude', colHeader: 'Latitude', colType: 'coordinate', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'longitude', colHeader: 'Longitude', colType: 'coordinate', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'coordinates', colHeader: 'Coordinates', colType: 'coordinate', editable: false },
            { colName: 'state', colHeader: 'State', colType: 'text', editable: false },
            { colName: 'county', colHeader: 'County', colType: 'text', editable: false },
        ],
        jsonColumnConfig: [
            {
                pointsTo: 'offload_lane',
                fields: [
                    {
                        colName: 'offload_lane_name',
                        colType: 'text',
                        placeholder: 'Add Lane',
                        required: true,
                        inputType: 'input',
                    },
                ],
                displayJsonCols: [
                    { colName: 'offload_lane_name', colHeader: 'Offload Lane', colType: 'text' },
                ],
            },
        ],
        sortOrder: {
            'is_active': 'desc',
            'market': 'asc',
            'station': 'asc',
        },
        idColumn: 'id',
        deletableRows: true,
        deletionRequiresApproval: true,
    },
    name_corrections: {
        tableName: 'Name Corrections',
        columnConfigs: [
            { colName: 'id', colHeader: 'ID', colType: 'number', editable: false },
            { colName: 'type', colHeader: 'Type', colType: 'text', editable: false },
            { colName: 'correct_name', colHeader: 'Correct Name', colType: 'text', editable: false },
            { colName: 'variations', colHeader: 'Variations', colType: 'text', editable: true },
        ],
        sortOrder: {
            'id': 'desc',
        },
        idColumn: 'id',
        deletableRows: false,
    },
    mobile_req_mailing_list: {
        tableName: 'Mobile Req. Mailing Lists',
        columnConfigs: [
            { colName: 'id', colHeader: 'ID', colType: 'number', editable: false },
            { colName: 'producer_name', colHeader: 'Producer', colType: 'text', editable: false },
            { colName: 'mailing_list', colHeader: 'Mailing List', colType: 'text', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
        ],
        sortOrder: {'producer_name': 'asc'},
        idColumn: 'id',
        deletableRows: true,
    },
    pumper_contact: {
        tableName: 'Pumper Contact Info',
        columnConfigs: [
            { colName: 'id', colHeader: 'ID', colType: 'number', editable: false },
            { colName: 'producer_name', colHeader: 'Producer', colType: 'text', editable: false },
            {
                colName: 'entity_name',
                actualColName: 'entity_id',
                colHeader: 'Entity Name',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: entityOptions,
                dependsOn: 'producer_id',
                getFilteredOptions: (producerId: number, allLeases: any[]) => {
                    const filtered = allLeases.filter(lease => {
                        return Number(lease.current_producer_id) === Number(producerId);
                    });
                    return filtered;
                },
            },
            { colName: 'name', colHeader: 'Name', colType: 'text', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'pumper_email', colHeader: 'Email', colType: 'text', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'pumper_phone', colHeader: 'Phone', colType: 'text', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] }
        ],
        sortOrder: { 
            'producer_name': 'asc',
            'name': 'asc',
        },
        idColumn: 'id',
        deletableRows: true,
    },
    trucking_rates: {
        tableName: 'Trucking Rates',
        columnConfigs: [
            { colName: 'id', colHeader: 'ID', colType: 'number', editable: false },
            { colName: 'mileage', colHeader: 'Mileage', colType: 'number', editable: false },
            { colName: 'endeavor', colHeader: 'Endeavor', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'penley', colHeader: 'Penley', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'anadarko', colHeader: 'Anadarko', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'cp_transport', colHeader: 'CP Transport', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'diamond_b', colHeader: 'Diamond B', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'emmaus', colHeader: 'Emmaus', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'guardian', colHeader: 'Guardian', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'heart_river', colHeader: 'Heart River', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'heart_river_nd', colHeader: 'Heart River ND', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'opa', colHeader: 'OPA', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'premier', colHeader: 'Premier', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'south_haven', colHeader: 'South Haven', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'iowa_tanklines', colHeader: 'Iowa Tanklines', editable: true, colType: 'number', requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
        ],
        idColumn: 'id',
        sortBy: ['']
    },
    calculated_routes: {
        tableName: 'Calculated Routes',
        columnConfigs: [
            { colName: 'id', colHeader: 'ID', colType: 'number', editable: false },
            { colName: 'producer', colHeader: 'Producer', colType: 'text', editable: false },
            { colName: 'lease', colHeader: 'Lease', colType: 'text', editable: false },
            { colName: 'market', colHeader: 'Market', colType: 'text', editable: false },
            { colName: 'station', colHeader: 'Station', colType: 'text', editable: false },
            { colName: 'distance_miles', colHeader: 'Mileage', colType: 'number', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'estimated_time_minutes', colHeader: 'Time (minutes)', colType: 'text', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { 
                colName: 'manual_override', 
                colHeader: 'Manual Override', 
                colType: 'boolean', 
                editable: true, 
                requiresApproval: true, 
                approvalLevel: 4, 
                rolesNotAllowedToEdit: ["viewer"] 
            },
        ],
        sortBy: ['producer', 'lease', 'market', 'station'],
        idColumn: 'id',
    },
    customer_reports: {
        tableName: 'Customer Reports',
        columnConfigs: [
            { 
                colName: 'id', 
                colHeader: 'ID', 
                colType: 'number', 
                editable: false 
            },
            { 
                colName: 'is_active',
                colHeader: 'Active', 
                colType: 'boolean', 
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                ], 
                editable: true, 
                requiresApproval: true, 
                approvalLevel: 4, 
                rolesNotAllowedToEdit: ["accountant","accounting_admin","viewer"]
            },
            { 
                colName: 'is_internal', 
                colHeader: 'Is Internal', 
                colType: 'boolean', 
                editable: true, 
                requiresApproval: true, 
                approvalLevel: 4, 
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                ], 
            },
            { 
                colName: 'customer', 
                colHeader: 'Customer', 
                colType: 'text', 
                editable: false
            },
            { 
                colName: 'mailing_list', 
                colHeader: 'Mailing List', 
                colType: 'text', 
                editable: true,
                requiresApproval: true, 
                editableLevel: 4,
                approvalLevel: 5, 
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]
            },
            { 
                colName: 'cc_list', 
                colHeader: 'CC List', 
                colType: 'text', 
                editable: true, 
                requiresApproval: true, 
                editableLevel: 4,
                approvalLevel: 5,  
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]
            },
            { 
                colName: 'email_subject', 
                colHeader: 'Email Subject', 
                colType: 'text', 
                editable: true, 
                requiresApproval: true, 
                editableLevel: 4,
                approvalLevel: 5,  
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]
            },
            { 
                colName: 'file_name', 
                colHeader: 'File Name', 
                colType: 'text', 
                editable: true, 
                requiresApproval: true, 
                editableLevel: 4,
                approvalLevel: 5,  
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]
            },
            { 
                colName: 'file_type', 
                colHeader: 'File Type',
                colType: 'text', 
                editable: true, 
                requiresApproval: true, 
                editableLevel: 4,
                approvalLevel: 5, 
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]
            },
            { 
                colName: 'sql_condition', 
                colHeader: 'Condition', 
                colType: 'text', 
                editable: true, 
                requiresApproval: true,
                editableLevel: 4,
                approvalLevel: 5,  
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { 
                colName: 'condition_new', 
                colHeader: 'Condition NEW', 
                colType: 'text', 
                editable: true,
                editableLevel: 4,
                approvalLevel: 5, 

            },
            { 
                colName: 'is_market',
                colHeader: 'Is Market',
                colType: 'boolean',
                editable: true, 
                requiresApproval: true, 
                editableLevel: 4,
                approvalLevel: 5,  
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                ], 
            },
            { 
                colName: 'columns_mapping', 
                colHeader: 'Columns', 
                colType: 'text', 
                editable: true, 
                requiresApproval: true, 
                editableLevel: 4,
                approvalLevel: 5, 
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]
            },
        ],
        sortOrder: { 
            'is_active': 'desc',
            'customer': 'asc',
        },
        idColumn: 'id',
        deletableRows: true,
    },
    carrier_definition: {
        tableName: 'Carrier Definition',
        columnConfigs: [
            { colName: 'id', colHeader: 'ID', colType: 'number', editable: false},
            { 
                colName: 'is_active', 
                colHeader: 'Active', 
                colType: 'boolean', 
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                ], 
                editable: true, 
                requiresApproval: true, 
                approvalLevel: 4, 
                rolesNotAllowedToEdit: ["accountant","accounting_admin","viewer"]
            },
            { colName: 'name', colHeader: 'Carrier Name', colType: 'text', editable: false},
            { colName: 'main_phone', colHeader: 'Main Phone', colType: 'text', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'email', colHeader: 'Email', colType: 'text', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'address', colHeader: 'Address', colType: 'text', editable: true, requiresApproval: true, approvalLevel: 4, rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"] },
            { colName: 'carrier_contacts', colHeader: 'Carrier Contacts', colType: 'json', editable: false, jsonCols: ['name', 'email', 'phone', 'position'] },
            { colName: 'operating_basin', colHeader: 'Operating Basin', colType: 'json', editable: false, jsonCols: ['basin'] },
            { colName: 'approved_product_types', colHeader: 'Approved Products', colType: 'json', editable: false, jsonCols: ['type'] },
            { colName: 'report_name', colHeader: 'Report Name', colType: 'text', editable: true, conditionalDisplay: (value) => { try { const arr = typeof value === 'string' ? JSON.parse(value) : value; return Array.isArray(arr) && arr.length > 0 ? arr[0] : (value ?? '-'); } catch { return value ?? '-'; } } },
            { colName: 'carrier_table', colHeader: 'SQL Table Name', colType: 'text', editable: true, requiresApproval: true, editableLevel: 5, approvalLevel: 6 },
            {
                colName: 'has_vac_truck',
                colHeader: 'Vacuum Truck',
                colType: 'boolean',
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                ],
                editable: true,
                requiresApproval: true,
                approvalLevel: 4,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"]
            },
        ],
        jsonColumnConfig: [
            {
                pointsTo: 'carrier_contacts',
                fields: [
                    { colName: 'name',     colType: 'text', placeholder: 'Name',     required: true,  inputType: 'input' },
                    { colName: 'email',    colType: 'text', placeholder: 'Email',    required: false, inputType: 'input' },
                    { colName: 'phone',    colType: 'text', placeholder: 'Phone',    required: false, inputType: 'input' },
                    { colName: 'position', colType: 'text', placeholder: 'Position', required: false, inputType: 'input' },
                ],
                displayJsonCols: [
                    { colName: 'name',     colHeader: 'Name',     colType: 'text' },
                    { colName: 'email',    colHeader: 'Email',    colType: 'text' },
                    { colName: 'phone',    colHeader: 'Phone',    colType: 'text' },
                    { colName: 'position', colHeader: 'Position', colType: 'text' },
                ],
            },
            {
                pointsTo: 'operating_basin',
                fields: [
                    { 
                        colName: 'basin', 
                        colType: 'text', 
                        placeholder: 'Basin', 
                        required: true, 
                        inputType: 'dropdown',
                        dropdownOptions: [
                            { value: 'Permian', label: 'Permian' },
                            { value: 'Eagle_Ford', label: 'Eagle Ford' },
                            { value: 'Bakken', label: 'Bakken' },
                            { value: 'MidCon', label: 'MidCon' },
                            { value: 'Marcellus', label: 'Marcellus' },
                            { value: 'Utica', label: 'Utica' },
                            { value: 'Haynesville', label: 'Haynesville' },
                            { value: 'DJ', label: 'DJ' }
                            

                        ],
                    },
                ],
                displayJsonCols: [
                    { colName: 'basin', colHeader: 'Basin', colType: 'text' },
                ],
            },
            {
                pointsTo: 'approved_product_types',
                fields: [
                    {
                        colName: 'type',
                        colType: 'text',
                        placeholder: 'Product Type',
                        required: true,
                        inputType: 'dropdown',
                        dropdownOptions: [
                            { value: 'Skim Oil',    label: 'Skim Oil' },
                            { value: 'Condensate',  label: 'Condensate' },
                            { value: 'Sour Crude',  label: 'Sour Crude' },
                            { value: 'NGL',         label: 'NGL' },
                            { value: 'Water',       label: 'Water' },
                            { value: 'Lease Crude', label: 'Lease Crude' },
                        ],
                    },
                ],
                displayJsonCols: [
                    { colName: 'type', colHeader: 'Product Type', colType: 'text' },
                ],
            },
        ],
        sortOrder: { 
            'is_active': 'desc',
            'name': 'asc',
        },
        idColumn: 'id',
    },
});

